import { memo } from "react";
import classNames from "classnames";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import {
    DocumentRetrievalExclusionReason,
    DOCUMENT_RETRIEVAL_EXCLUSION_LABELS,
} from "@common/types/task";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { Button } from "@design-system/Button/Button";
import { Menu } from "@design-system/Menu/Menu.lib";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { CheckIcon, MinusIcon, UploadIcon } from "@icons";
import {
    DocumentRetrievalTableCallbacks,
    DocumentRetrievalTableRow,
} from "../documentRetrievalViewConfig";
import {
    getVerificationDescription,
    getVerificationDisplay,
} from "../documentReviewStatus";
import { MonthStatus } from "../StatusCellContent";
import styles from "../DocumentRetrievalTaskBody.module.scss";

function isTableCallbacks(
    value: object,
): value is DocumentRetrievalTableCallbacks {
    return (
        "onReview" in value &&
        "onExclude" in value &&
        "onClearExclusion" in value &&
        "onRetry" in value &&
        "onUpload" in value &&
        "onRemoveFromTask" in value &&
        "isRemovingDocument" in value &&
        typeof value.onReview === "function" &&
        typeof value.onExclude === "function" &&
        typeof value.onClearExclusion === "function" &&
        typeof value.onRetry === "function" &&
        typeof value.onUpload === "function" &&
        typeof value.onRemoveFromTask === "function" &&
        typeof value.isRemovingDocument === "function"
    );
}

function readTableCallbacks(
    table: CellContext<
        ViewTableFeatures,
        DocumentRetrievalTableRow,
        string
    >["table"],
): DocumentRetrievalTableCallbacks | undefined {
    const meta = table.options.meta;
    if (!meta || !("callbacks" in meta)) {
        return undefined;
    }
    const { callbacks } = meta;
    if (
        callbacks &&
        typeof callbacks === "object" &&
        isTableCallbacks(callbacks)
    ) {
        return callbacks;
    }
    return undefined;
}

const EXCLUSION_OPTIONS = Object.values(DocumentRetrievalExclusionReason).map(
    (value) => ({ value, label: DOCUMENT_RETRIEVAL_EXCLUSION_LABELS[value] }),
);

function TextCell({ value }: { value: string | null }) {
    return (
        <ViewTableCell>
            <BreakableText
                text={value ?? ""}
                showTooltip
                className={classNames(styles.filenameCell, {
                    [styles.filenameCellEmpty]: !value,
                })}
            />
        </ViewTableCell>
    );
}

export const DocumentRetrievalRowNumberCell = memo(
    ({
        getValue,
    }: CellContext<
        ViewTableFeatures,
        DocumentRetrievalTableRow,
        number | null
    >) => (
        <ViewTableCell>
            <span className={styles.rowNumber}>{getValue() ?? ""}</span>
        </ViewTableCell>
    ),
);
DocumentRetrievalRowNumberCell.displayName = "DocumentRetrievalRowNumberCell";

export const DocumentRetrievalPeriodCell = memo(
    ({
        row,
    }: CellContext<
        ViewTableFeatures,
        DocumentRetrievalTableRow,
        string | null
    >) => {
        const { periodLabel } = row.original;
        return (
            <ViewTableCell>
                <span>{periodLabel}</span>
            </ViewTableCell>
        );
    },
);
DocumentRetrievalPeriodCell.displayName = "DocumentRetrievalPeriodCell";

export const DocumentRetrievalFileNameCell = memo(
    ({
        row,
    }: CellContext<
        ViewTableFeatures,
        DocumentRetrievalTableRow,
        string | null
    >) => {
        const { fileName, kind } = row.original;
        return (
            <ViewTableCell>
                <BreakableText
                    text={fileName ?? ""}
                    showTooltip
                    className={classNames({
                        [styles.unverifiedFilename]: kind === "unverified",
                        [styles.filenameCell]: kind === "month",
                        [styles.filenameCellEmpty]: !fileName,
                    })}
                />
            </ViewTableCell>
        );
    },
);
DocumentRetrievalFileNameCell.displayName = "DocumentRetrievalFileNameCell";

export const DocumentRetrievalStatementPeriodCell = memo(
    ({
        getValue,
    }: CellContext<
        ViewTableFeatures,
        DocumentRetrievalTableRow,
        string | null
    >) => <TextCell value={getValue()} />,
);
DocumentRetrievalStatementPeriodCell.displayName =
    "DocumentRetrievalStatementPeriodCell";

export const DocumentRetrievalAccountCell = memo(
    ({
        getValue,
    }: CellContext<
        ViewTableFeatures,
        DocumentRetrievalTableRow,
        string | null
    >) => <TextCell value={getValue()} />,
);
DocumentRetrievalAccountCell.displayName = "DocumentRetrievalAccountCell";

function MonthStatusContent({
    status,
    exclusionReason,
}: {
    status: MonthStatus;
    exclusionReason: DocumentRetrievalExclusionReason | null;
}) {
    if (status === "verified") {
        return (
            <div className={classNames(styles.statusMessage, "text-gray-900")}>
                <CheckIcon className="icon-color-gray-900" />
                <BreakableText
                    text="Matched"
                    showTooltip
                    className={styles.statusText}
                />
            </div>
        );
    }

    if (status === "excluded" && exclusionReason) {
        return (
            <div className={classNames(styles.statusMessage, "text-gray-900")}>
                <MinusIcon className="icon-color-gray-900" />
                <BreakableText
                    text={DOCUMENT_RETRIEVAL_EXCLUSION_LABELS[exclusionReason]}
                    showTooltip
                    className={styles.statusText}
                />
            </div>
        );
    }

    return (
        <div className={classNames(styles.statusMessage, "text-blue-600")}>
            <UploadIcon className="icon-color-blue-600" />
            <BreakableText
                text="Awaiting upload"
                showTooltip
                className={styles.statusText}
            />
        </div>
    );
}

export const DocumentRetrievalStatusCell = memo(
    ({
        row,
    }: CellContext<
        ViewTableFeatures,
        DocumentRetrievalTableRow,
        MonthStatus | null
    >) => {
        const { kind, status, exclusionReason, verification } = row.original;

        if (kind === "unverified" && verification) {
            const display = getVerificationDisplay(verification);
            const description = getVerificationDescription(verification);
            const content = (
                <div
                    className={classNames(
                        styles.unverifiedStatus,
                        display.className,
                    )}
                >
                    {display.icon}
                    <BreakableText
                        text={display.label}
                        showTooltip
                        className={styles.statusText}
                    />
                </div>
            );

            if (!description) {
                return <ViewTableCell>{content}</ViewTableCell>;
            }

            return (
                <ViewTableCell>
                    <Tooltip content={description} side="bottom">
                        {content}
                    </Tooltip>
                </ViewTableCell>
            );
        }

        if (!status) {
            return <ViewTableCell />;
        }

        return (
            <ViewTableCell>
                <MonthStatusContent
                    status={status}
                    exclusionReason={exclusionReason}
                />
            </ViewTableCell>
        );
    },
);
DocumentRetrievalStatusCell.displayName = "DocumentRetrievalStatusCell";

export const DocumentRetrievalActionsCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, DocumentRetrievalTableRow, string>) => {
        const { refuseIfViewOnly } = useViewOnlyRefusal();
        const callbacks = readTableCallbacks(table);
        const { kind, status, exclusionReason, monthKey, documentId } =
            row.original;
        const isExcluded = status === "excluded" && exclusionReason !== null;
        const showUpload = kind === "month" && documentId == null;
        const showReview =
            kind === "unverified" ||
            (kind === "month" && status === "verified" && documentId != null);

        const isRemoving =
            documentId != null && !!callbacks?.isRemovingDocument(documentId);

        if (showReview) {
            return (
                <ViewTableCell captureClicks>
                    <div className={styles.actionCell}>
                        <Button
                            variant="primary"
                            size="md"
                            className={viewTableStyles.showOnHover}
                            onClick={() => {
                                if (documentId != null) {
                                    callbacks?.onReview(documentId);
                                }
                            }}
                        >
                            Review
                        </Button>
                        <Menu.More
                            size="md"
                            align="start"
                            aria-label="Row actions"
                            className={viewTableStyles.showOnHover}
                        >
                            <Menu.Item
                                destructive
                                disabled={isRemoving}
                                onClick={() => {
                                    if (
                                        refuseIfViewOnly() ||
                                        documentId == null
                                    ) {
                                        return;
                                    }
                                    callbacks?.onRemoveFromTask(documentId);
                                }}
                            >
                                {isRemoving
                                    ? "Removing..."
                                    : "Remove from task"}
                            </Menu.Item>
                        </Menu.More>
                    </div>
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell captureClicks>
                <div className={styles.actionCell}>
                    {showUpload && (
                        <Button
                            variant="primary"
                            size="md"
                            className={viewTableStyles.showOnHover}
                            onClick={() => {
                                if (refuseIfViewOnly()) {
                                    return;
                                }
                                callbacks?.onUpload();
                            }}
                        >
                            Upload
                        </Button>
                    )}
                    <Menu.More
                        size="md"
                        align="start"
                        aria-label="Row actions"
                        className={viewTableStyles.showOnHover}
                    >
                        {EXCLUSION_OPTIONS.map((option) => (
                            <Menu.Item
                                key={option.value}
                                onClick={() => {
                                    if (refuseIfViewOnly() || !monthKey) {
                                        return;
                                    }
                                    callbacks?.onExclude(
                                        monthKey,
                                        option.value,
                                    );
                                }}
                            >
                                {option.label}
                            </Menu.Item>
                        ))}
                        {isExcluded && monthKey && (
                            <>
                                <Menu.Separator />
                                <Menu.Item
                                    onClick={() => {
                                        if (refuseIfViewOnly()) {
                                            return;
                                        }
                                        callbacks?.onClearExclusion(monthKey);
                                    }}
                                >
                                    Clear
                                </Menu.Item>
                            </>
                        )}
                    </Menu.More>
                </div>
            </ViewTableCell>
        );
    },
);
DocumentRetrievalActionsCell.displayName = "DocumentRetrievalActionsCell";
